import { apiClient } from '@/lib/api/client';

export interface OnboardingSubscriptionLike {
  id?: string | null;
  status?: string | null;
  metadata?: string | Record<string, unknown> | null;
  trial_end?: string | Date | null;
  trial_days?: number | string | null;
  plan?: { slug?: string | null; price?: string | number | null } | null;
}

const SELF_SERVICE_TRIAL_TYPE = 'self_service_trial';

const TRIAL_PLAN_SLUG = 'trial-15-day';

function readMetadataType(metadata: OnboardingSubscriptionLike['metadata']): string | null {
  if (!metadata) return null;
  let parsed: unknown = metadata;
  if (typeof metadata === 'string') {
    try {
      parsed = JSON.parse(metadata);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const type = (parsed as Record<string, unknown>).type;
  return typeof type === 'string' ? type : null;
}

function looksLikeFreeTrial(subscription: OnboardingSubscriptionLike): boolean {
  if (subscription.plan?.slug === TRIAL_PLAN_SLUG) return true;

  const hasTrialWindow =
    subscription.trial_end != null || Number(subscription.trial_days) > 0;
  if (!hasTrialWindow) return false;

  const price = subscription.plan?.price;
  if (price == null || price === '') return false;
  const parsed = Number(price);
  return Number.isFinite(parsed) && parsed === 0;
}

export function subscriptionProvesPriorSetup(
  subscription: OnboardingSubscriptionLike | null | undefined
): boolean {
  if (!subscription?.id) return false;
  if (readMetadataType(subscription.metadata) === SELF_SERVICE_TRIAL_TYPE) return false;
  if (looksLikeFreeTrial(subscription)) return false;

  const status = (subscription.status || '').toString().toLowerCase();
  if (status === 'trialing' || status === 'none') return false;

  return true;
}

export interface FirstRunOnboardingUser {
  role?: string | null;
  is_agency?: boolean | null;
  is_super_admin?: boolean | null;
  is_organization_owner?: boolean | null;
}

export function isFirstRunOnboardingAudience(
  user: FirstRunOnboardingUser | null | undefined
): boolean {
  if (!user) return false;
  if (user.is_super_admin) return false;
  if (user.is_agency) return false;

  const role = (user.role || '').toString().toUpperCase();
  return role === 'ADMIN' || user.is_organization_owner === true;
}

export const ONBOARDING_COMPLETED_KEY = 'onboarding_survey_completed';
const LEGACY_MIRROR_VALUE = 'true';
export const ONBOARDING_COMPLETE_PENDING_KEY = 'onboarding_complete_pending';
const NEW_SIGNUP_KEY = 'new_signup';
const NEXA_INTRO_PENDING_KEY = 'nexa_widget_intro_pending';

function readKey(key: string): string | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeKey(key: string, value: string): void {
  try {
    window.localStorage?.setItem(key, value);
  } catch {
  }
}

function removeKey(key: string): void {
  try {
    window.localStorage?.removeItem(key);
  } catch {
  }
}

function mirrorMatches(raw: string | null, organizationId?: string | null): boolean {
  if (!raw) return false;
  if (!organizationId) return raw === LEGACY_MIRROR_VALUE;
  return raw === organizationId;
}

export function isOnboardingCompletedLocally(organizationId?: string | null): boolean {
  return mirrorMatches(readKey(ONBOARDING_COMPLETED_KEY), organizationId);
}

export function isOnboardingCompletePending(): boolean {
  return readKey(ONBOARDING_COMPLETE_PENDING_KEY) === 'true';
}

export function isNewSignupPending(): boolean {
  return readKey(NEW_SIGNUP_KEY) === 'true';
}

export function markNewSignupPending(): void {
  writeKey(NEW_SIGNUP_KEY, 'true');
}

export function resetOnboardingCompletionForNewAccount(): void {
  removeKey(ONBOARDING_COMPLETED_KEY);
  removeKey(ONBOARDING_COMPLETE_PENDING_KEY);
}

export function cacheOnboardingCompleted(organizationId?: string | null): void {
  writeKey(ONBOARDING_COMPLETED_KEY, organizationId || LEGACY_MIRROR_VALUE);
  removeKey(ONBOARDING_COMPLETE_PENDING_KEY);
}

export async function markOnboardingComplete(organizationId?: string | null): Promise<void> {
  const alreadyComplete = isOnboardingCompletedLocally(organizationId);
  const needsRetry = isOnboardingCompletePending();

  if (alreadyComplete && !needsRetry) return;

  writeKey(ONBOARDING_COMPLETED_KEY, organizationId || LEGACY_MIRROR_VALUE);
  removeKey(NEW_SIGNUP_KEY);
  removeKey(NEXA_INTRO_PENDING_KEY);


  try {
    await apiClient.post('/onboarding/complete');
    removeKey(ONBOARDING_COMPLETE_PENDING_KEY);
  } catch {
    writeKey(ONBOARDING_COMPLETE_PENDING_KEY, 'true');
  }
}
